import React from 'react';
import * as echarts from 'echarts/core';
import {
    LineChart as EChartsLineChart,
} from 'echarts/charts';
import {
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import EchartsWrapper from '@/components/echartsWrapper';

// 注册必要的组件
echarts.use([
    EChartsLineChart,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    GridComponent,
    CanvasRenderer,
]);

interface LineChartData {
    name: string;
    value: Array<[number | string, number]>; // [类别索引, 数值]
}

interface LineChartProps {
    /** 图表数据 */
    data?: LineChartData[];
    /** 图表标题 */
    title?: string;
    /** x轴标签 */
    xAxisLabel?: string;
    /** y轴标签 */
    yAxisLabel?: string;
    /** 图表样式 */
    style?: React.CSSProperties;
    /** 线条颜色 */
    color?: string | string[];
    /** 是否显示数值标签 */
    showLabel?: boolean;
    /** 是否平滑曲线 */
    smooth?: boolean;
    /** 是否显示符号点 */
    showSymbol?: boolean;
    /** 线条宽度 */
    lineWidth?: number;
}

const LineChart: React.FC<LineChartProps> = ({
    data = [{
        name: '--',
        value: [],
    }],
    title = '折线图',
    xAxisLabel = '',
    yAxisLabel = '',
    style = { width: '100%', height: '400px' },
    color = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de'],
    showLabel = false,
    smooth = true,
    showSymbol = true,
    lineWidth = 2
}) => {
    const chartOption = {
        title: {
            text: title,
            left: 'center',
            textStyle: {
                fontSize: 16,
                fontWeight: 'bold'
            }
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            }
        },
        legend: {
            data: data.map(item => item.name),
            top: '10%'
        },
        grid: {
            left: '6%',
            right: '8%',
            bottom: '6%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            name: xAxisLabel,
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
                fontSize: 12
            },
            boundaryGap: false
        },
        yAxis: {
            type: 'value',
            name: yAxisLabel,
            nameLocation: 'middle',
            nameGap: 50,
            axisLabel: {
                fontSize: 12
            }
        },
        series: data.map((item, index) => ({
            name: item.name,
            type: 'line',
            data: item.value,
            smooth: smooth,
            symbol: showSymbol ? 'circle' : 'none',
            symbolSize: 6,
            lineStyle: {
                color: Array.isArray(color) ? color[index % color.length] : color,
                width: lineWidth
            },
            itemStyle: {
                color: Array.isArray(color) ? color[index % color.length] : color,
                borderColor: '#fff',
                borderWidth: 2
            },
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {
                        offset: 0,
                        color: Array.isArray(color) ?
                            echarts.color.modifyAlpha(color[index % color.length], 0.3) :
                            echarts.color.modifyAlpha(color as string, 0.3)
                    },
                    {
                        offset: 1,
                        color: Array.isArray(color) ?
                            echarts.color.modifyAlpha(color[index % color.length], 0.1) :
                            echarts.color.modifyAlpha(color as string, 0.1)
                    }
                ])
            },
            label: {
                show: showLabel,
                position: 'top',
                fontSize: 12,
                color: '#666'
            },
            emphasis: {
                scale: true,
                scaleSize: 8
            }
        }))
    };

    return (
        <>
            {data && data.length > 0 ? (
                <EchartsWrapper
                    chartOption={chartOption}
                    style={style}
                />
            ) : (
                <div style={style}>
                    <h4>{title}</h4>
                    <p>暂无数据</p>
                </div>
            )}
        </>
    );
};

export default LineChart;